<script lang="ts">
	// Trash (SAY-1463 / SAY-1820). A collapsible sidebar section listing
	// soft-deleted chats, each with Restore and "Delete forever"; the header
	// offers "Empty trash" and, in selection mode, every row gets a checkbox so
	// the bulk bar can restore / delete many at once. PERMANENT deletion is
	// gated behind a confirm because it drops the session, its history, and its
	// worktree for good.
	import SidebarSection from './SidebarSection.svelte';
	import Spinner from './Spinner.svelte';
	import ConfirmDeleteDialog from './ConfirmDeleteDialog.svelte';
	import SelectCheckbox from './SelectCheckbox.svelte';
	import Trash2Icon from '@lucide/svelte/icons/trash-2';
	import RotateCcwIcon from '@lucide/svelte/icons/rotate-ccw';
	import { getSessions, markRestored, markTrashed, removeSession, type SessionRow } from '$lib/sessions-store.svelte';
	import { restoreSession, deleteSession } from '$lib/api';
	import { forgetSession } from '$lib/folders-store.svelte';
	import { forgetTab } from '$lib/chat-tabs.svelte';
	import { isSelected, isSelectionMode, toggleSelected } from '$lib/chat-selection.svelte';
	import { toast } from 'svelte-sonner';

	const sessions = getSessions();

	// Trashed chats, most-recently-trashed first.
	const trashed = $derived(sessions.filter((s) => s.deletedAt).sort((a, b) => (b.deletedAt ?? 0) - (a.deletedAt ?? 0)));

	let open = $state(false);
	let restoring = $state<string | null>(null);
	// What the confirm dialog is about: one chat, or the whole trash.
	let pending = $state<{ kind: 'one'; row: SessionRow } | { kind: 'all' } | null>(null);
	let confirmOpen = $state(false);
	let deleting = $state(false);

	// Closing the dialog (Cancel / esc) drops the pending target.
	$effect(() => {
		if (!confirmOpen && !deleting) pending = null;
	});

	function ask(target: { kind: 'one'; row: SessionRow } | { kind: 'all' }): void {
		pending = target;
		confirmOpen = true;
	}

	function label(row: SessionRow): string {
		return row.name ?? row.sessionId.slice(0, 8);
	}

	async function restoreOne(row: SessionRow): Promise<void> {
		if (restoring) return;
		restoring = row.sessionId;
		markRestored(row.sessionId); // optimistic — row leaves the Trash at once
		try {
			await restoreSession(row.sessionId);
			toast.success('Restored', { description: label(row) });
		} catch (err) {
			markTrashed(row.sessionId); // put it back on failure
			toast.error('Restore failed', { description: err instanceof Error ? err.message : String(err) });
		} finally {
			restoring = null;
		}
	}

	async function purge(id: string): Promise<void> {
		await deleteSession(id);
		removeSession(id);
		forgetSession(id);
		forgetTab(id);
	}

	async function confirmDelete(): Promise<void> {
		const p = pending;
		if (!p || deleting) return;
		deleting = true;
		try {
			if (p.kind === 'one') {
				await purge(p.row.sessionId);
				toast.success('Deleted permanently', { description: label(p.row) });
			} else {
				let ok = 0;
				let failed = 0;
				for (const row of [...trashed]) {
					try {
						await purge(row.sessionId);
						ok += 1;
					} catch (err) {
						failed += 1;
						console.warn('[trash] delete failed for', row.sessionId, err);
					}
				}
				if (failed === 0) toast.success(`Emptied the trash (${ok} ${ok === 1 ? 'chat' : 'chats'})`);
				else toast.error(`Deleted ${ok}, failed for ${failed}`);
			}
			confirmOpen = false;
		} catch (err) {
			toast.error('Delete failed', { description: err instanceof Error ? err.message : 'Server did not delete the chat.' });
		} finally {
			deleting = false;
		}
	}
</script>

{#if trashed.length > 0}
	<SidebarSection name="Trash ({trashed.length})" id="trash" bind:open class="mt-1.5">
		{#snippet action()}
			<button
				type="button"
				class="flex size-7 items-center justify-center rounded-lg text-gray-300 transition-colors duration-100 hover:text-red-600 dark:text-gray-600 dark:hover:text-red-400"
				aria-label="Empty trash"
				title="Empty trash"
				data-testid="empty-trash"
				onclick={() => ask({ kind: 'all' })}
			>
				<Trash2Icon class="size-3.5" strokeWidth={1.7} aria-hidden="true" />
			</button>
		{/snippet}
		<div class="pt-1.5" role="list">
			{#each trashed as s (s.sessionId)}
				<div class="group/reveal relative w-full" role="listitem" data-testid="trash-item" data-session-id={s.sessionId}>
					<div
						class="flex w-full justify-between rounded-xl px-2 py-1.5 whitespace-nowrap text-gray-500 transition hover:bg-gray-100 dark:text-gray-500 dark:hover:bg-gray-900 {isSelected(s.sessionId)
							? 'bg-black/[0.035] dark:bg-white/[0.045]'
							: ''}"
					>
						{#if isSelectionMode()}
							<SelectCheckbox
								class="mr-2 self-center"
								checked={isSelected(s.sessionId)}
								label="Select trashed chat {label(s)}"
								testid="trash-select"
								onchange={(on) => toggleSelected(s.sessionId, on)}
							/>
						{/if}
						<span dir="auto" class="h-5 min-w-0 flex-1 self-center truncate pr-12 text-left">{label(s)}</span>
					</div>
					{#if !isSelectionMode()}
						<div class="hover-reveal absolute inset-y-0 right-1 mr-1.5 flex items-center space-x-1.5 self-center">
							<button
								type="button"
								class="flex size-5 items-center justify-center self-center transition hover:text-gray-900 dark:hover:text-white"
								aria-label="Restore chat"
								title="Restore"
								disabled={restoring === s.sessionId}
								onclick={() => void restoreOne(s)}
							>
								{#if restoring === s.sessionId}
									<Spinner class="size-3.5" />
								{:else}
									<RotateCcwIcon class="size-3.5" strokeWidth={1.7} aria-hidden="true" />
								{/if}
							</button>
							<button
								type="button"
								class="flex size-5 items-center justify-center self-center transition hover:text-gray-900 dark:hover:text-white"
								aria-label="Delete forever"
								title="Delete forever"
								onclick={() => ask({ kind: 'one', row: s })}
							>
								<Trash2Icon class="size-3.5" strokeWidth={1.7} aria-hidden="true" />
							</button>
						</div>
					{/if}
				</div>
			{/each}
		</div>
	</SidebarSection>
{/if}

<ConfirmDeleteDialog
	bind:open={confirmOpen}
	title={pending?.kind === 'all' ? 'Empty the trash?' : 'Delete chat?'}
	busy={deleting}
	onConfirm={() => void confirmDelete()}
>
	{#if pending?.kind === 'all'}
		This will permanently delete all {trashed.length} {trashed.length === 1 ? 'chat' : 'chats'} in the trash with their history and
		any files they created. This cannot be undone.
	{:else}
		This will permanently delete <span class="font-medium text-gray-700 dark:text-gray-300"
			>“{pending?.kind === 'one' ? label(pending.row) : ''}”</span
		> with its history and any files it created. This cannot be undone.
	{/if}
</ConfirmDeleteDialog>
